import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  /** @type {{ id: number; x: number; y: number; t: number; index: number } | null} */
  #tapTrack = null;

  /** Dedupe zoom when both pointer-tap and delegated click fire for the same gesture. */
  #lastZoomDedupeAt = 0;
  #lastZoomDedupeIndex = -1;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.addEventListener('pointerdown', this.#onTapPointerDown, { signal, capture: true });
    this.addEventListener('pointerup', this.#onTapPointerUp, { signal, capture: true });
    this.addEventListener('pointercancel', this.#onTapPointerCancel, { signal, capture: true });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * @param {Event} event
   * @returns {HTMLElement | null}
   */
  #zoomableSlideFromEvent(event) {
    const el = event.target;
    if (!(el instanceof Element)) return null;
    return el.closest(
      'slideshow-slide.product-media-container--zoomable, .media-gallery__grid > li.product-media-container--zoomable'
    );
  }

  /**
   * @param {HTMLElement} slide
   * @returns {number}
   */
  #indexOfZoomableSlide(slide) {
    if (slide.matches('.media-gallery__grid > li')) {
      const media = this.refs.media;
      if (!media) return -1;
      const list = Array.isArray(media) ? media : [media];
      return list.indexOf(slide);
    }
    const slides = this.refs.slideshow?.slides;
    if (!slides?.length) return -1;
    return slides.indexOf(slide);
  }

  #onTapPointerDown = (event) => {
    if (!this.refs.zoomDialogComponent) return;
    if (!event.isPrimary) return;
    const slide = this.#zoomableSlideFromEvent(event);
    if (!slide) return;
    const index = this.#indexOfZoomableSlide(slide);
    if (index < 0) return;
    this.#tapTrack = {
      id: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      t: performance.now(),
      index,
    };
  };

  #onTapPointerUp = (event) => {
    const tr = this.#tapTrack;
    if (!tr || event.pointerId !== tr.id) return;
    const { index, x, y, t } = tr;
    this.#tapTrack = null;

    const dx = Math.abs(event.clientX - x);
    const dy = Math.abs(event.clientY - y);
    const dt = performance.now() - t;
    if (dx > 28 || dy > 28 || dt > 900) return;

    this.zoom(index, event);
  };

  #onTapPointerCancel = (event) => {
    if (this.#tapTrack?.id === event.pointerId) this.#tapTrack = null;
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    if (!this.refs.zoomDialogComponent) return;

    const now = performance.now();
    if (index === this.#lastZoomDedupeIndex && now - this.#lastZoomDedupeAt < 350) {
      return;
    }
    this.#lastZoomDedupeIndex = index;
    this.#lastZoomDedupeAt = now;

    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
